import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  MagnifyingGlass,
  Eye,
  UserPlus,
  LockSimple,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";

const TAG_COLORS = {
  Green: 'bg-green-100 text-green-800 border-green-300',
  Yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Orange: 'bg-orange-100 text-orange-800 border-orange-300',
  Red: 'bg-red-100 text-red-800 border-red-300',
  Black: 'bg-gray-800 text-gray-100 border-gray-900'
};

export default function PatientsListPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('name');
  const [filterEvent, setFilterEvent] = useState('all');
  const [events, setEvents] = useState([]);

  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients`);
      setPatients(data);
    } catch (err) {
      console.warn('Patient list fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPatients();
    axios.get(`${API_URL}/api/events`).then(res => setEvents(res.data)).catch(() => {});
  }, [fetchPatients]);

  const handleDeletePatient = async (e, patientId, patientName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the record for ${patientName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/patients/${patientId}`);
      toast.success('Record deleted');
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete record');
    }
  };

  const filteredPatients = patients.filter(patient => {
    // Event filter
    if (filterEvent !== 'all' && patient.event_name !== filterEvent) return false;
    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    if (filterType === 'name') {
      return (
        patient.first_name?.toLowerCase().includes(search) ||
        patient.last_name?.toLowerCase().includes(search) ||
        `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(search) ||
        patient.form_number?.toLowerCase().includes(search)
      );
    } else if (filterType === 'dob') {
      return patient.dob?.includes(search);
    } else if (filterType === 'service_date') {
      return patient.form_date?.includes(search) || patient.created_at?.includes(search);
    }
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="patients-list-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <img src={LOGO_1} alt="Logo 1" className="h-8 object-contain" />
                <img src={LOGO_2} alt="Logo 2" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                {user?.first_name} {user?.last_name}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Patient Records</h1>
            <p className="text-slate-500 text-sm mt-1">
              {patients.length} total records
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={filterType} onValueChange={(v) => { setFilterType(v); setSearchTerm(''); }}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="search-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">By Name</SelectItem>
                <SelectItem value="dob">By Date of Birth</SelectItem>
                <SelectItem value="service_date">By Date of Service</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type={filterType === 'dob' || filterType === 'service_date' ? 'date' : 'text'}
                placeholder={filterType === 'name' ? 'Search by name or form #...' : filterType === 'dob' ? 'Select DOB...' : 'Select service date...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 rounded-sm border-slate-300"
                data-testid="search-patients-input"
              />
            </div>
            <Select value={filterEvent} onValueChange={setFilterEvent}>
              <SelectTrigger className="w-52 rounded-sm" data-testid="search-event-filter"><SelectValue placeholder="All Events" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {events.map(ev => <SelectItem key={ev.id} value={ev.name}>{ev.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Link to="/patient/new">
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="new-patient-button">
                <UserPlus size={18} className="mr-2" />
                New Patient
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Loading patients...</p>
          </div>
        ) : filteredPatients.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">
                {searchTerm ? 'No patients found matching your search.' : 'No patient records yet.'}
              </p>
              <Link to="/patient/new">
                <Button className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm">
                  Create First Patient Record
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Form #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Patient Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Triage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Event
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Form Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Disposition
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredPatients.map((patient) => {
                      const isOpen = patient.form_status !== 'closed';
                      const canEdit = isOpen || user?.role === 'admin';
                      return (
                      <tr 
                        key={patient.id} 
                        className={`transition-colors ${canEdit ? 'hover:bg-blue-50 cursor-pointer' : 'bg-slate-50/50 opacity-75'}`}
                        onClick={() => canEdit ? navigate(`/patient/${patient.id}/edit`) : null}
                        data-testid={`patient-row-${patient.id}`}
                      >
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono font-semibold text-slate-700 bg-slate-100 px-2 py-1 rounded" data-testid={`form-number-${patient.id}`}>
                            {patient.form_number || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`font-medium ${isOpen ? 'text-slate-900' : 'text-slate-500'}`}>
                            {patient.first_name} {patient.last_name}
                            {patient.form_type === 'medication' && (
                              <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-100 text-blue-600" data-testid={`med-badge-${patient.id}`}>
                                OTC
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${TAG_COLORS[patient.tag_color] || 'bg-slate-100 text-slate-600'}`}>
                            {patient.tag_color}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {patient.event_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          {patient.form_status === 'closed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700" data-testid={`status-${patient.id}`}>
                              <LockSimple size={12} /> Closed
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700" data-testid={`status-${patient.id}`}>
                              Open
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {patient.is_transported ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                              Transport
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-slate-100 text-slate-600">
                              No Transport
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatDate(patient.created_at)}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {isOpen ? (
                            <Link to={`/patient/${patient.id}/edit`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="rounded-sm text-blue-600 hover:text-blue-800"
                                data-testid={`edit-patient-${patient.id}`}
                              >
                                <PencilSimple size={16} className="mr-1" />
                                Edit
                              </Button>
                            </Link>
                          ) : user?.role === 'admin' ? (
                            <Link to={`/patient/${patient.id}/edit`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="rounded-sm text-amber-600 hover:text-amber-800"
                                data-testid={`amend-patient-${patient.id}`}
                              >
                                <PencilSimple size={16} className="mr-1" />
                                Amend
                              </Button>
                            </Link>
                          ) : (
                            <Link to={`/patient/${patient.id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="rounded-sm"
                                data-testid={`view-patient-${patient.id}`}
                              >
                                <Eye size={16} className="mr-1" />
                                View
                              </Button>
                            </Link>
                          )}
                          {user?.role === 'admin' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="rounded-sm text-red-500 hover:text-red-700 hover:bg-red-50 ml-1"
                              onClick={(e) => handleDeletePatient(e, patient.id, `${patient.first_name} ${patient.last_name}`)}
                              data-testid={`delete-patient-${patient.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
